import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Form,
  Spin,
  Card,
  message,
  Input,
  Button,
} from 'antd';
import Select from 'components/Select';
import NumericInput from 'components/NumericInput';
import Table from 'components/Table';
import {
  deleteTipoDeConvenio,
  getTiposDeConvenios,
  patchTipoDeConvenio,
  postTipoDeConvenio,
} from 'api/recaudacion/tiposDeConvenio';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import { onError } from 'utils/handlers';
import { ActionsNames, getModelPermission, hasActionPermission } from 'utils/permission';
import { isMutable } from 'utils/estadosGlobales';
import { toUpper } from 'utils/normalizers';
import { getFuncionarios } from 'api/usuarios/funcionarios';
import { ciudadanoToString } from 'utils/padronesToString';
import { getDepartamentos } from 'api/configuracion/departamentos';
import { getCatalogoDePadrones, getClasesDeVencimientoDeParcialidades } from 'api/catalogos';
import { getConfiguracionesDeSegmentosDeFolios } from 'api/configuracion/configuracionesDeFolios';

const catalogsModels = ['tipodeconvenio'];

const labelsComportamiento = {
  1: 'Las parcialidades se cobrarán cada fin de mes',
  2: 'Las parcialidades se cobrarán el mismo día de creación del convenio',
  3: 'Las parcialidades se cobrarán cada inicio de mes',
  4: 'Se le dejará al usuario la opción de elegir el día de cobro de las parcialidades',
};

const TiposDeConvenios = () => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [permission, setPermission] = useState();
  const [mutable, setMutable] = useState(true);

  const [funcionarios, setFuncionarios] = useState([]);
  const [padrones, setPadrones] = useState();
  const [departamentos, setDepartamentos] = useState([]);
  const [clasesDeVencimientos, setClasesDeVencimientos] = useState([]);
  const [claseDeVencimientoSelected, setClaseDeVencimientoSelected] = useState(0);
  const [folios, setFolios] = useState([]);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    form.resetFields();
    setClaseDeVencimientoSelected(0);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposDeConvenios();
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const _permission = getModelPermission(catalogsModels[0]);
      const _hasAllViewPermission = catalogsModels
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setHasAllViewPermission(_hasAllViewPermission);
        setPermission(_permission);
      }
    };
    const _fetchData = async () => {
      const [
        _data,
        _funcionarios,
        _padrones,
        _departamentos,
        _clasesDeVencimientoDeParcialidades,
        _segmentosDeFolios,
      ] = await Promise.all([
        getTiposDeConvenios(),
        getFuncionarios(),
        getCatalogoDePadrones(),
        getDepartamentos(),
        getClasesDeVencimientoDeParcialidades(),
        getConfiguracionesDeSegmentosDeFolios({ estados_globales: 4 }),
      ]);
      if (mounted) {
        setFolios(_segmentosDeFolios);
        setPadrones(_padrones);
        setFuncionarios(_funcionarios);
        setDepartamentos(_departamentos);
        setClasesDeVencimientos(_clasesDeVencimientoDeParcialidades);
        setData(_data);
        setLoading(false);
      }
    };

    _fetchData();
    validatePermission();
    return () => { mounted = false; };
  }, []);

  const columns = [
    {
      titleText: 'Descripcíón',
      key: 'descripcion',
      dataIndex: 'descripcion',
      width: 350,
    },
    {
      titleText: 'Límite de mensualidades vencidas',
      key: 'cancelacion_por_pagos_vencidos',
      dataIndex: 'cancelacion_por_pagos_vencidos',
      width: 350,
    },
    {
      titleText: 'Cantidad máxima de parcialidades',
      key: 'cantidad_maxima_de_parcialidades',
      dataIndex: 'cantidad_maxima_de_parcialidades',
      width: 350,
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    setMutable(isMutable(record.estados_globales));
    setClaseDeVencimientoSelected(record?.configuracion_de_vencimiento_parcialidades || 0);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = () => {
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const deleteItem = async () => {
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteTipoDeConvenio(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      values.clase_de_vencimiento_de_parcialidades = values
        .configuracion_de_vencimiento_parcialidades;
      const saved = await (key
        ? patchTipoDeConvenio(key, values) : postTipoDeConvenio(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rules = {
    required: [{
      required: true,
    }],
  };

  const renderOptionsFuncionarios = (e) => `${e?.puesto_principal?.descripcion
    ? `${e?.puesto_principal?.descripcion} - ` : ''}${ciudadanoToString(e)}`;

  return (
    <Row align="center" justify="center" className="container">
      <Col span={24}>
        <Spin tip="Cargando..." spinning={loading}>
          {!visible ? (
            <Table
              cols={columns}
              data={data}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              rowKey="id"
              permission={permission}
              hasAllViewPermission={hasAllViewPermission}
            />
          ) : (
            <Card
              className="form-container no-padding"
              title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Convenio`}
              extra={(
                <FormSubmitControls
                  onFinish={onFinish}
                  onCancel={onCancel}
                  mutable={mutable}
                  onCancelText="Cancelar"
                  permission={permission}
                />
              )}
              bordered={false}
            >
              <Form
                layout="vertical"
                name="general"
                form={form}
                onFinish={() => onFinish()}
                scrollToFirstError
                initialValues={{
                  estados_globales: 1,
                  aplicar_parcialidades_completas: true,
                }}
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="descripcion"
                      rules={rules.required}
                      label="Descripción"
                      hasFeedback
                      normalize={toUpper}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={16}>
                    <Form.Item
                      name="requisitos_del_convenio"
                      rules={rules.required}
                      label="Requisitos del convenio"
                      hasFeedback
                    >
                      <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="padron"
                      rules={rules.required}
                      label="Padrón"
                      hasFeedback
                    >
                      <Select dataSource={padrones} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={16} lg={8}>
                    <NumericInput
                      name="cantidad_maxima_de_parcialidades"
                      rules={rules.required}
                      label="Cantidad máxima de parcialidades"
                      hasFeedback
                    />
                  </Col>
                  <Col xs={24} sm={24} md={16} lg={8}>
                    <NumericInput
                      name="cancelacion_por_pagos_vencidos"
                      rules={rules.required}
                      label="Límite de mensualidades vencidas"
                      hasFeedback
                    />
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="unidad_responsable"
                      label="Unidad Responsable"
                      hasFeedback
                    >
                      <Select dataSource={departamentos} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="configuracion_de_vencimiento_parcialidades"
                      rules={rules.required}
                      label="Clase de vencimiento de parcialidades"
                      hasFeedback
                      tooltip={labelsComportamiento?.[claseDeVencimientoSelected] ?? ''}
                    >
                      <Select
                        dataSource={clasesDeVencimientos}
                        onChange={setClaseDeVencimientoSelected}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="aplicar_parcialidades_completas"
                      rules={rules.required}
                      label="¿Aplicar parcialidades completas?"
                    >
                      <Select trueFalse />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="generar_recargos_parcialidad"
                      rules={rules.required}
                      label="Generar Recargos Parcialidad"
                    >
                      <Select trueFalse />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="reportes_de_convenio_id"
                      rules={rules.required}
                      label="Identificador de Reporte de convenio"
                      hasFeedback
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="firmas_de_funcionarios"
                      label="Firmas de Funcionarios"
                      hasFeedback
                    >
                      <Select
                        dataSource={funcionarios}
                        mode="multiple"
                        render={renderOptionsFuncionarios}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="configuracion_de_folio_del_convenio"
                      label="Foliador"
                      tooltip="Formatos de folio autorizados"
                      hasFeedback
                    >
                      <Select dataSource={folios} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Form>
            </Card>
          )}
        </Spin>
      </Col>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="El registro seleccionado"
      />
    </Row>
  );
};

export default TiposDeConvenios;
